import { Ng2StateDeclaration, Transition } from '@uirouter/angular';
import { StateDeclaration } from '@app-types/StateDeclaration';
import { catchError, firstValueFrom, of, switchMap } from 'rxjs';
import { IFBTokenInfo } from '@interfaces';
import { CompanyTalentScoutInfo } from './data-access/job-share.models';
import { JobShareListService } from './data-access/job-share.service';

// The PRL never has an “st” in its parameters
// We only use "st" to obtain prt.

const dynamicParams = {
  params: {
    currentJob: { dynamic: true },
    referraltoken: { dynamic: true },
    referralId: { dynamic: true },
    birdly: { dynamic: true }
  }
};

export const STATES = {
  JOB_LIST: 'job-list',
  JOB_LIST_UNAVAILABLE: 'job-list-unavailable'
};

const ERROR_STATE = { state: STATES.JOB_LIST_UNAVAILABLE };

function addPRTorShowErrorPage(transition: Transition): any {
  if (transition.from().name === STATES.JOB_LIST) {
    return;
  }

  const { st, prt, referraltoken } = transition.params();

  if (!prt && referraltoken) {
    return resolvePersonalReferralTokenFromReferralToken(transition, referraltoken); //reminder email case
  }

  if (!st && prt) {
    return validatePersonalReferralLinkAccess(transition, prt); //validate if personal referral link is enabled
  }

  if (prt) {
    return;
  }

  if (!st) {
    return ERROR_STATE;
  }

  return resolvePersonalReferralTokenFromShareTokenOrShareToken(transition, st); //normal share job case
}

function resolvePersonalReferralTokenFromReferralToken(transition: Transition, referraltoken: string) {
  const jobShareListService = transition.injector().get(JobShareListService) as JobShareListService;
  return firstValueFrom(
    jobShareListService.getTokenInfoV1(referraltoken).pipe(
      switchMap(tokenInfo => {
        const prtValue = tokenInfo?.personalReferralToken;
        if (prtValue) {
          return of({
            state: STATES.JOB_LIST,
            params: { ...transition.params(), prt: prtValue }
          });
        }
        return of(ERROR_STATE);
      }),
      catchError(() => of(ERROR_STATE))
    )
  ) as Promise<any>;
}

function validatePersonalReferralLinkAccess(transition: Transition, prt: string) {
  const jobShareListService = transition.injector().get(JobShareListService);
  return firstValueFrom(
    jobShareListService.getInfoCompanyTalentScout(prt).pipe(
      switchMap((dataInfo: CompanyTalentScoutInfo) => {
        const prlEnabled = dataInfo.personalReferralLinkEnabled;
        if (prlEnabled) {
          return of(true);
        }
        return of(ERROR_STATE);
      }),
      catchError(() => of(ERROR_STATE))
    )
  ) as Promise<any>;
}

function resolvePersonalReferralTokenFromShareTokenOrShareToken(transition: Transition, token: string) {
  const jobShareListService = transition.injector().get(JobShareListService);
  return firstValueFrom(
    jobShareListService.getTokenInfoV2(token).pipe(
      switchMap((tokenInfo: IFBTokenInfo) => {
        const prtValue = tokenInfo?.personalReferralToken;
        if (prtValue) {
          return of({
            state: STATES.JOB_LIST,
            params: { ...transition.params(), prt: prtValue }
          });
        }
        return of(ERROR_STATE);
      }),
      catchError(() => of(ERROR_STATE))
    )
  ) as Promise<any>;
}

export const jobShareState: Ng2StateDeclaration[] = [
  {
    name: 'job-redirect',
    url: '/job/:jobId?st&birdly&referraltoken&referralId',
    redirectTo: (transition: Transition) => {
      return {
        state: 'job-list',
        params: {
          ...transition.params(),
          prt: ''
        }
      };
    }
  },
  {
    name: 'job-list.**',
    url: '/r',
    lazyLoad: async () => {
      return {
        states: [
          {
            name: STATES.JOB_LIST,
            url: '/r/:prt?jobId&st&currentJob&referraltoken&referralId&birdly',
            ...dynamicParams,
            data: {
              bodyClass: ['app-scrollable']
            },
            loadComponent: () =>
              import('./components/job-share-list/job-share-list.component').then(m => m.JobShareListComponent),
            redirectTo: addPRTorShowErrorPage
          },
          ,
          {
            name: STATES.JOB_LIST_UNAVAILABLE,
            url: '/r/unavailable',
            loadComponent: () =>
              import('./components/job-share-list-unavailable/job-share-list-unavailable.component').then(
                m => m.JobShareListUnavailableComponent
              )
          }
        ] as StateDeclaration[]
      };
    }
  }
];
